import { useRef, useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  normaliseMyShowSlug,
  normaliseMyShowState,
} from "@/lib/myShow";

// Utility function to fetch state from server-side API on initial load
const fetchStateFromServer = async (userId, initialLoadCompletedRef) => {
  if (!userId) return;

  try {
    const response = await fetch(
      `/api/myshow/get?userId=${userId}&cacheBust=${Date.now()}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    initialLoadCompletedRef.current = true;

    if (!data.success) return;

    useMyShowStore.setState(normaliseMyShowState(data.data));
    // console.log("Fetched state from server:", data);
  } catch (error) {
    initialLoadCompletedRef.current = true;
    console.error("Failed to fetch state from server:", error);
  }
};

// Utility function to sync state with server-side API
const syncStateWithServer = async (userId) => {
  if (!userId) {
    return;
  }

  try {
    const currentState = useMyShowStore.getState();

    const newState = normaliseMyShowState(currentState);

    // console.log("Syncing state with server:", newState);

    const updateResponse = await fetch(
      `/api/myshow/set?cacheBust=${Date.now()}`,
      {
        method: "POST",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, state: newState }),
      }
    );

    const data = await updateResponse.json();

    // console.log("State synced with server:", data);
  } catch (error) {
    console.error("Failed to sync state with server:", error);
  }
};

// Persistent State using local storage
export const useMyShowStore = create(
  persist(
    (set, get) => ({
      // Showbags
      myShowbags: [],
      addShowbag: (show) => {
        const slug = normaliseMyShowSlug(show);
        if (!slug) return;

        set((state) => {
          const newState = { myShowbags: [...state.myShowbags, slug] };
          return newState;
        });
      },
      removeShowbag: (show) => {
        set((state) => {
          const newState = {
            myShowbags: state.myShowbags.filter((s) => s !== show),
          };
          return newState;
        });
      },

      // Rides
      myRides: [],
      addRide: (ride) => {
        const slug = normaliseMyShowSlug(ride);
        if (!slug) return;

        set((state) => {
          const newState = { myRides: [...state.myRides, slug] };
          return newState;
        });
      },
      removeRide: (ride) => {
        set((state) => {
          const newState = {
            myRides: state.myRides.filter((r) => r !== ride),
          };
          return newState;
        });
      },

      // Events
      myEvents: [],
      addEvent: (event) => {
        const slug = normaliseMyShowSlug(event);
        if (!slug) return;

        set((state) => {
          const newState = { myEvents: [...state.myEvents, slug] };
          return newState;
        });
      },
      removeEvent: (event) => {
        set((state) => {
          const newState = {
            myEvents: state.myEvents.filter((e) => e !== event),
          };
          return newState;
        });
      },

      // Food
      myFood: [],
      addFood: (food) => {
        const slug = normaliseMyShowSlug(food);
        if (!slug) return;

        set((state) => {
          const newState = { myFood: [...state.myFood, slug] };
          return newState;
        });
      },
      removeFood: (food) => {
        set((state) => {
          const newState = {
            myFood: state.myFood.filter((f) => f !== food),
          };
          return newState;
        });
      },
      reset: () =>
        set({
          myShowbags: [],
          myRides: [],
          myEvents: [],
          myFood: [],
        }),
    }),
    {
      name: "myShow", // name of the item in the storage (must be unique)
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState || {}),
        ...normaliseMyShowState(persistedState),
      }),
    }
  )
);

const clearStore = () => {
  useMyShowStore.persist.clearStorage();
  useMyShowStore.getState().reset();
};

// Custom hook to fetch and set state from Kinde on login
export const useFetchStateFromKinde = ({ isAuthenticated, isLoading, user }) => {
  const userId = user?.id ?? null;

  // Ref to track if the initial load has completed
  const initialLoadCompletedRef = useRef(false);

  useEffect(() => {
    if (userId) {
      // console.log("Initial load, fetching state from server...");
      fetchStateFromServer(userId, initialLoadCompletedRef);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && initialLoadCompletedRef.current) {
      // console.log("Local State changed, syncing with server...", userId);
      syncStateWithServer(userId);
    }
  }, [userId, useMyShowStore((state) => state)]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      clearStore();
    }
  }, [isAuthenticated, isLoading]);
};
